// src/OwnerAuditChecklist.js — the owner's audit checklist (System Settings tab).
//
// Everything here comes from /api/owner-checklist, which only answers the owner
// (OWNER_EMAILS + Microsoft login, checked on the server). The audit text is not
// bundled into this file on purpose: it describes unfixed security holes, and
// the console's JavaScript is downloadable by anyone who finds the site.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { supabase } from './supabaseClient';
import { ink, surface, status as tone } from './designTokens';

import { prettyStatus } from './labels';
const MAX_SEND = 25;

const AUDIT_STATUS = {
  complete: { label: 'Complete', color: tone.success, bg: tone.successBg },
  partial: { label: 'Partial', color: tone.warning, bg: tone.warningBg },
  ui_only: { label: 'UI only', color: tone.info, bg: tone.infoBg },
  backend_only: { label: 'Backend only', color: tone.info, bg: tone.infoBg },
  not_started: { label: 'Not started', color: tone.danger, bg: tone.dangerBg },
  needs_testing: { label: 'Needs testing', color: tone.info, bg: tone.infoBg },
  unknown: { label: "Can't verify", color: tone.neutral, bg: tone.neutralBg },
};

// What the fix run said about itself, after the owner sent an item to Claude.
//
// Green and red are the two the owner asked for. Amber sits between them
// because "partly fixed" is the answer that comes back most often and is the
// one most worth not rounding up: the §2 run closed the email hole in three
// sign-in functions and left the verification flow, the SMS consent record and
// the notification toggles untouched. Calling that green would be a lie the
// checklist tells you every time you look at it.
const FIX_OUTCOME = {
  fixed: { label: 'Fixed', color: tone.success, bg: tone.successBg },
  partial: { label: 'Partly fixed', color: tone.warning, bg: tone.warningBg },
  not_fixed: { label: 'Not fixed', color: tone.danger, bg: tone.dangerBg },
  pending: { label: 'Waiting', color: tone.neutral, bg: tone.neutralBg },
};

// How far along one item is, as a single number and a plain-English label.
//
// Two chips — "Audit found: Partial" beside "Fix run says: Fixed" — made the
// page look like it was arguing with itself, and reading a row meant knowing
// that one was a snapshot from the audit and the other was current. A bar says
// the same thing in the way everything else on a dashboard says it.
//
// The fix run wins when there is one: it looked at the code more recently than
// the audit did. Both are still shown in Details & notes, so nothing is lost —
// only the need to interpret them at a glance.
export function itemProgress(item, outcome, verification) {
  // A check beats a claim.
  //
  // Everything else on this page is someone's word: the audit's, or a fix
  // run's. A verification is the database answering about itself — the column
  // is there or it is not. When one exists it decides the row, whatever anyone
  // reported. Issue #9 reported twelve §3 items fixed; none of their columns
  // exist, and without this they would still be showing green.
  if (verification) {
    return verification.passed
      ? { pct: 100, label: 'Verified', color: tone.success, verified: true }
      : { pct: 0, label: 'Not in the database', color: tone.danger, verified: true };
  }
  return claimedProgress(item, outcome);
}

function claimedProgress(item, outcome) {
  const fromAudit = {
    complete: { pct: 100, label: 'Done', color: tone.success },
    needs_testing: { pct: 75, label: 'Built, needs testing', color: tone.info },
    backend_only: { pct: 50, label: 'Backend only', color: tone.warning },
    ui_only: { pct: 40, label: 'Screen only, nothing behind it', color: tone.warning },
    partial: { pct: 50, label: 'Half built', color: tone.warning },
    not_started: { pct: 0, label: 'Not started', color: tone.danger },
    unknown: { pct: 0, label: 'Never checked', color: tone.neutral },
  };
  const audit = fromAudit[item.status] || fromAudit.unknown;

  if (outcome) {
    if (outcome.status === 'fixed' && outcome.branch_merged !== false) {
      return { pct: 100, label: 'Done', color: tone.success };
    }
    if (outcome.status === 'fixed' && outcome.branch_merged === false) {
      // Written and reported, but not in main. Deliberately short of done:
      // nothing on an unmerged branch is running in the app.
      return { pct: 70, label: 'Written, not merged yet', color: tone.warning };
    }
    if (outcome.status === 'partial') {
      return { pct: 50, label: 'Part done', color: tone.warning };
    }
    if (outcome.status === 'not_fixed') {
      // A run looked and did not fix it. That is not progress, but it is not
      // ignorance either — keep whatever the audit found.
      return { pct: audit.pct, label: 'Looked at, still open', color: tone.danger };
    }
    return { pct: audit.pct, label: 'Waiting on the fix run', color: tone.neutral };
  }
  return audit;
}

const fixedWhen = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const OWNER_STATUS = [
  { value: '', label: 'Not decided' },
  { value: 'fix_now', label: 'Fix now' },
  { value: 'later', label: 'Later' },
  { value: 'not_needed', label: "Don't need it" },
];

async function api(method, body) {
  const { data: sess } = await supabase.auth.getSession();
  const res = await fetch('/api/owner-checklist', {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sess?.session?.access_token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error([json.error, json.hint].filter(Boolean).join(' ') || `Server returned ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return json;
}

// Loads the checklist. Resolves to null when the caller is not the owner, so the
// settings page can leave the tab out entirely.
export async function loadOwnerChecklist() {
  try {
    return await api('GET');
  } catch (err) {
    // 401/403 mean "you are not the owner", and hiding the tab is right.
    //
    // 503 means the feature is not configured — OWNER_EMAILS is unset — and
    // that is NOT the same thing. Returning null for it made the tab vanish
    // with no tab, no message and nothing to search for, so the only way to
    // learn the cause was to read the function's source. Surfacing it means
    // the screen can say what to do.
    if (err.status === 401 || err.status === 403) return null;
    return { error: err.message, hint: err.hint, notConfigured: err.status === 503 };
  }
}

function copyText(items, rows, note) {
  const lines = [
    'Please fix these LIVLIFE audit items (full audit: docs/LIVLIFE_APP_AUDIT.md). Open a pull request; do not merge.',
  ];
  if (note) lines.push('', `My overall note: ${note}`);
  for (const it of items) {
    const r = rows[it.key] || {};
    lines.push('', `§${it.sectionNumber} ${it.sectionTitle} — ${it.label}`, `Audit status: ${it.statusText || prettyStatus(it.status)}`);
    if (it.notes) lines.push(`Audit notes: ${it.notes}`);
    if (r.notes) lines.push(`My notes: ${r.notes}`);
    if (r.wanted) lines.push(`What I want done: ${r.wanted}`);
  }
  return lines.join('\n');
}

// One column layout, shared by the header row and every item row. Defined once
// because the only thing keeping a header above its column is that both use
// the same template — two copies drift the moment either is edited.
const GRID = {
  xs: '1fr',
  md: 'minmax(0, 1fr) 104px 168px 148px 272px 84px',
};

function HeadCell({ children, align }) {
  return (
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700, color: ink[500], textTransform: 'uppercase',
        letterSpacing: '.04em', textAlign: align || 'left',
      }}
    >
      {children}
    </Typography>
  );
}

// One bar, used on every row and every section header, so a glance down the
// page compares like with like.
function ProgressBar({ pct, color, height = 8 }) {
  return (
    <Box
      sx={{
        height, borderRadius: height / 2, bgcolor: surface.line,
        overflow: 'hidden', width: '100%',
      }}
    >
      <Box sx={{ width: `${Math.max(0, Math.min(100, pct))}%`, height: '100%', bgcolor: color }} />
    </Box>
  );
}

function ItemRow({ item, row, outcome, verification, selected, onToggleSelect, onSave }) {
  const [notes, setNotes] = useState(row.notes || '');
  const [wanted, setWanted] = useState(row.wanted || '');
  const [open, setOpen] = useState(false);
  useEffect(() => { setNotes(row.notes || ''); setWanted(row.wanted || ''); }, [row.notes, row.wanted]);

  // Settled means the bar reads 100%, whoever put it there.
  //
  // This used to require a fix-run outcome, so "Create account", "Log in" and
  // "Log out" — done according to the audit, never sent to a run — still
  // offered "Fix now" and "Send Issue". Anything already done should offer
  // the same thing: a way to say it broke.
  const isSettled = itemProgress(item, outcome, verification).pct === 100;
  const [reopened, setReopened] = useState(false);

  const prog = itemProgress(item, outcome, verification);
  const st = AUDIT_STATUS[item.status] || AUDIT_STATUS.unknown;
  const fix = outcome ? (FIX_OUTCOME[outcome.status] || FIX_OUTCOME.pending) : null;
  const fixedAt = outcome ? fixedWhen(outcome.reported_at) : null;
  const save = (patch) => onSave(item.key, { done: row.done, ownerStatus: row.owner_status || '', notes, wanted, ...patch });

  return (
    <Box sx={{ borderTop: `1px solid ${surface.line}`, py: 1.25 }}>
      {/* Six columns, the same six as the header above. Every cell is
          vertically centred and sits in a fixed track, so scanning down a
          column compares like with like. */}
      <Box sx={{ display: 'grid', gap: 1.5, alignItems: 'center', gridTemplateColumns: GRID }}>
        {/* ITEM */}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontWeight: 600, color: ink[900], cursor: 'pointer', lineHeight: 1.3 }}
            onClick={() => setOpen((o) => !o)}
          >
            {item.label}
          </Typography>
          {/* The idea a fix run had for this item, while it still had the real
              code in front of it. On the row rather than behind Details — one
              nobody reads is not worth asking for. */}
          {outcome && outcome.idea && (
            <Box sx={{ mt: 0.5, display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
              <Chip
                size="small"
                label={outcome.idea_effort ? `Idea · ${outcome.idea_effort}` : 'Idea'}
                sx={{ bgcolor: tone.infoBg, color: tone.info, fontWeight: 600, flexShrink: 0, height: 20 }}
              />
              <Typography variant="caption" sx={{ color: ink[700] }}>{outcome.idea}</Typography>
            </Box>
          )}
        </Box>

        {/* NEEDED FOR */}
        <Typography variant="caption" sx={{ color: ink[500] }}>{item.need || '—'}</Typography>

        {/* PROGRESS */}
        <Box sx={{ minWidth: 0 }}>
          <ProgressBar pct={prog.pct} color={prog.color} />
          <Typography
            variant="caption"
            sx={{ color: ink[500], fontVariantNumeric: 'tabular-nums', mt: 0.25, display: 'block' }}
          >
            {prog.pct}%{fixedAt ? ` · ${fixedAt}` : ''}
          </Typography>
        </Box>

        {/* STATUS — and when a check decided it, what the check found.
            A red row that does not say why is just an accusation. */}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ color: prog.color, fontWeight: 600, lineHeight: 1.3 }}>
            {prog.label}
          </Typography>
          {verification && (
            <Typography variant="caption" sx={{ color: ink[500], display: 'block', lineHeight: 1.25 }}>
              {verification.detail}
            </Typography>
          )}
        </Box>

        {/* ACTION — a finished item needs a way to say it broke, not a way to
            send the same work twice. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {isSettled && !reopened ? (
            <Button
              size="small"
              variant="outlined"
              onClick={() => { setReopened(true); setOpen(true); }}
            >
              Report bug
            </Button>
          ) : (
            <>
              <TextField
                select
                size="small"
                value={row.owner_status || ''}
                onChange={(e) => save({ ownerStatus: e.target.value })}
                sx={{ width: 124 }}
                inputProps={{ 'aria-label': 'My decision' }}
              >
                {OWNER_STATUS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
              <FormControlLabel
                sx={{ mr: 0 }}
                control={<Checkbox size="small" checked={selected} onChange={() => onToggleSelect(item.key)} />}
                label={<Typography variant="caption">Send</Typography>}
              />
            </>
          )}
        </Box>

        {/* DETAILS */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="small" onClick={() => setOpen((o) => !o)}>
            {open ? 'Hide' : 'Details'}
          </Button>
        </Box>
      </Box>
      {open && (
        <Box sx={{ pt: 1, display: 'grid', gap: 1.5 }}>
          {reopened && (
            <Alert
              severity="warning"
              action={<Button size="small" onClick={() => setReopened(false)}>Cancel</Button>}
            >
              Say what still goes wrong in <strong>What I want done</strong> below — what you did, what you
              expected, what happened — then tick <strong>Send Issue</strong> and send it. The run sees
              that this item was reported fixed before, so it knows it is looking for a regression.
            </Alert>
          )}
          {outcome && (
            <Box sx={{ bgcolor: fix.bg, border: `1px solid ${fix.color}33`, borderRadius: 1, p: 1.25 }}>
              <Typography variant="body2" sx={{ color: fix.color, fontWeight: 600 }}>
                {fix.label}
                {fixedAt ? ` — reported ${fixedAt}` : ''}
              </Typography>
              {outcome.comment && (
                <Typography variant="body2" sx={{ color: ink[700], mt: 0.5 }}>{outcome.comment}</Typography>
              )}
              <Box sx={{ mt: 0.5, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {outcome.issue_url && (
                  <Link href={outcome.issue_url} target="_blank" rel="noopener noreferrer" variant="caption">
                    Issue #{outcome.issue_number}
                  </Link>
                )}
                {outcome.branch_url && (
                  <Link href={outcome.branch_url} target="_blank" rel="noopener noreferrer" variant="caption">
                    Branch
                  </Link>
                )}
              </Box>
              {/* The fix run has not built or tested anything it changed, and says
                  so itself. The chip is a report, not a guarantee. */}
              <Typography variant="caption" sx={{ display: 'block', color: ink[500], mt: 0.5 }}>
                {outcome.branch_merged === false
                  ? 'Reported by the fix run, on a branch that has not been merged. Nothing here is running in your app yet.'
                  : 'Reported by the fix run — not verified by a build or a test.'}
              </Typography>
            </Box>
          )}
          {/* The two readings the bar combines. Kept here rather than on the
              row: useful when you want them, noise when you don't. */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ color: ink[500] }}>Audit found</Typography>
            <Chip size="small" label={st.label} sx={{ bgcolor: st.bg, color: st.color, fontWeight: 600 }} />
            {fix && (
              <>
                <Typography variant="caption" sx={{ color: ink[500], ml: 1 }}>Fix run says</Typography>
                <Chip size="small" label={fix.label} sx={{ bgcolor: fix.bg, color: fix.color, fontWeight: 600 }} />
              </>
            )}
          </Box>
          {item.notes && (
            <Typography variant="body2" sx={{ color: ink[700] }}>
              <strong>Audit notes:</strong> {item.notes}
            </Typography>
          )}
          {item.evidence && (
            <Typography variant="body2" sx={{ color: ink[500], fontFamily: 'monospace', fontSize: 12, overflowWrap: 'anywhere' }}>
              Evidence: {item.evidence}
            </Typography>
          )}
          <TextField
            label="My notes"
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => notes !== (row.notes || '') && save({ notes })}
          />
          <TextField
            label="What I think needs to be done"
            multiline
            minRows={2}
            value={wanted}
            onChange={(e) => setWanted(e.target.value)}
            onBlur={() => wanted !== (row.wanted || '') && save({ wanted })}
          />
        </Box>
      )}
    </Box>
  );
}

export default function OwnerAuditChecklist({ initial }) {
  const [data, setData] = useState(initial);
  const [rows, setRows] = useState(() => Object.fromEntries((initial.state || []).map((r) => [r.item_key, r])));
  // Keyed off `data` rather than `initial`: sending a new request refreshes
  // data, and the items just sent should immediately show as waiting.
  const outcomes = useMemo(
    () => Object.fromEntries((data.outcomes || []).map((o) => [o.item_key, o])),
    [data.outcomes],
  );

  // Keyed the same way as outcomes. Absent means no check exists for that
  // item, which is not the same as a check that failed.
  const verifications = useMemo(
    () => Object.fromEntries((data.verifications || []).map((v) => [v.item_key, v])),
    [data.verifications],
  );
  const [selected, setSelected] = useState([]);
  const [filter, setFilter] = useState('open');
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendNote, setSendNote] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [copied, setCopied] = useState(null);

  const allItems = useMemo(
    () =>
      data.sections.flatMap((s) =>
        s.items.map((it) => ({ ...it, sectionNumber: s.number, sectionTitle: s.title })),
      ),
    [data.sections],
  );
  const itemByKey = useMemo(() => new Map(allItems.map((i) => [i.key, i])), [allItems]);
  // Progress, from what is actually known rather than from a tick box.
  //
  // This counted rows[key].done — the owner's checkbox, now removed — so it
  // read 0 of 879 and told her nothing. An item is done when the audit found it
  // complete, or when a fix run has reported it fixed AND that work is merged.
  // Reported-but-unmerged counts as in progress, because it is not in the app.
  const progress = useMemo(() => {
    const ps = allItems.map((i) => itemProgress(i, outcomes[i.key], verifications[i.key]));
    const total = Math.max(ps.length, 1);
    const done = ps.filter((p) => p.pct === 100).length;
    const notDone = ps.filter((p) => p.pct === 0).length;

    // What sending has actually produced.
    //
    // The headline number barely moves no matter how much is sent, and that
    // looks broken when it is not: it is the mean over every item, and most of
    // them have never been sent. Getting two items fixed out of twenty shifts
    // a 879-item mean by about a tenth of a percent. These counts move every
    // time a run reports, so there is something that answers "did that do
    // anything".
    const sentKeys = Object.keys(outcomes);
    const sent = {
      total: sentKeys.length,
      fixed: sentKeys.filter((k) => outcomes[k].status === 'fixed').length,
      partial: sentKeys.filter((k) => outcomes[k].status === 'partial').length,
      notFixed: sentKeys.filter((k) => outcomes[k].status === 'not_fixed').length,
      waiting: sentKeys.filter((k) => outcomes[k].status === 'pending').length,
      unmerged: sentKeys.filter((k) => outcomes[k].branch_merged === false).length,
    };
    const untouched = allItems.length - sent.total;
    return {
      done,
      sent,
      untouched,
      inProgress: ps.length - done - notDone,
      notDone,
      total: allItems.length,
      // The mean, not the count of finished items: half-built work is real
      // progress and a bar that ignores it understates where the app is.
      pctOverall: Math.round(ps.reduce((a, p) => a + p.pct, 0) / total),
      pctDone: (done / total) * 100,
      pctInProgress: ((ps.length - done - notDone) / total) * 100,
      // How much of that is actually checked, rather than asserted.
      //
      // Most of this page's "done" comes from the audit document calling an
      // item complete — 363 of the 879 at the time of writing — with nothing
      // confirming it. That document has been wrong repeatedly: it told five
      // separate fix runs that applied migrations were not applied. A number
      // that cannot tell "we checked" from "it says so" is not worth reading.
      verified: allItems.filter((it) => verifications[it.key]).length,
      verifiedPassing: allItems.filter((it) => verifications[it.key]?.passed).length,
    };
  }, [allItems, outcomes, verifications]);

  const matches = useCallback(
    (it) => {
      const r = rows[it.key] || {};
      if (filter === 'open' && r.done) return false;
      if (filter === 'done' && !r.done) return false;
      if (filter === 'fix_now' && r.owner_status !== 'fix_now') return false;
      if (filter === 'problems' && !['partial', 'not_started', 'ui_only', 'backend_only'].includes(it.status)) return false;
      if (search) {
        const q = search.toLowerCase();
        return [it.label, it.notes, it.sectionTitle, r.notes, r.wanted].some((t) => (t || '').toLowerCase().includes(q));
      }
      return true;
    },
    [rows, filter, search],
  );

  const onSave = async (key, patch) => {
    const prev = rows[key];
    const optimistic = {
      ...(prev || {}),
      item_key: key,
      done: patch.done,
      owner_status: patch.ownerStatus || null,
      notes: patch.notes,
      wanted: patch.wanted,
    };
    setRows((r) => ({ ...r, [key]: optimistic }));
    setSavingKey(key);
    setError(null);
    try {
      const { saved } = await api('POST', { action: 'save', key, ...patch });
      setRows((r) => ({ ...r, [key]: saved }));
    } catch (err) {
      setRows((r) => ({ ...r, [key]: prev }));
      setError(`That change was not saved. ${err.message}`);
    } finally {
      setSavingKey(null);
    }
  };

  const toggleSelect = (key) =>
    setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));

  // Whole-section actions. A section can hold forty items, and ticking each
  // one to send them is forty clicks for a single decision.
  //
  // Both skip anything already at 100%: those rows show Report bug instead of
  // a tick box, and sweeping them back into a send would ask for work that is
  // already done.
  const selectMany = (keys, on) =>
    setSelected((s) => (on ? [...new Set([...s, ...keys])] : s.filter((k) => !keys.includes(k))));

  // One save per item, because that is what the endpoint takes, but fired
  // together and awaited as a group so the row stops looking half-applied.
  const setManyOwnerStatus = async (keys, value) => {
    setSavingKey('bulk');
    try {
      await Promise.all(keys.map((key) => {
        const r = rows[key] || {};
        return api('POST', {
          action: 'save',
          key,
          done: r.done,
          ownerStatus: value,
          notes: r.notes || '',
          wanted: r.wanted || '',
        });
      }));
      setRows((prev) => {
        const next = { ...prev };
        for (const key of keys) next[key] = { ...(next[key] || { item_key: key }), owner_status: value };
        return next;
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingKey(null);
    }
  };

  const selectedItems = selected.map((k) => itemByKey.get(k)).filter(Boolean);

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      const res = await api('POST', { action: 'send', keys: selected, note: sendNote });
      setSent(res);
      setData((d) => ({ ...d, requests: [res.request, ...(d.requests || [])].filter(Boolean) }));
      setSelected([]);
      setSendNote('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const copy = async () => {
    const text = copyText(selectedItems, rows, sendNote);
    try {
      await navigator.clipboard.writeText(text);
      setCopied('Copied. Paste it into a new Claude Code session for this repository.');
    } catch {
      setCopied('Could not reach the clipboard. Your browser blocked it.');
    }
  };

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Audit checklist</Typography>
      </Box>

      {/* One bar, three states, in the same colours as the chips below it, so
          the summary and the rows cannot tell different stories. */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {progress.pctOverall}% ready
            <Typography component="span" variant="body2" sx={{ color: ink[500], ml: 1, fontWeight: 400 }}>
              {progress.done} of {progress.total} items fully done
            </Typography>
          </Typography>
          {savingKey && <Typography variant="caption" sx={{ color: ink[500] }}>Saving…</Typography>}
        </Box>

        <Typography variant="body2" sx={{ color: ink[500], mb: 0.75 }}>
          <strong>{progress.verifiedPassing}</strong> of these are confirmed against the live
          database{progress.verified > progress.verifiedPassing
            ? `, and ${progress.verified - progress.verifiedPassing} are checked and failing`
            : ''}. The rest is what the audit document and the fix runs say about
          themselves, which has been wrong often enough to be worth separating.
        </Typography>

        <Box
          sx={{
            display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden',
            bgcolor: tone.dangerBg, border: `1px solid ${surface.line}`,
          }}
        >
          <Box sx={{ width: `${progress.pctDone}%`, bgcolor: tone.success }} />
          <Box sx={{ width: `${progress.pctInProgress}%`, bgcolor: tone.warning }} />
        </Box>

        <Box sx={{ display: 'flex', gap: 2.5, mt: 0.75, flexWrap: 'wrap' }}>
          {[
            { c: tone.success, n: progress.done, l: 'Done', h: 'audit says complete, or a fix run fixed it and the work is merged' },
            { c: tone.warning, n: progress.inProgress, l: 'In progress', h: 'part-built, or fixed on a branch that has not been merged' },
            { c: tone.danger, n: progress.notDone, l: 'Not done', h: 'nothing built, or a fix run reported it could not be done' },
          ].map((s) => (
            <Box key={s.l} title={s.h} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.c }} />
              <Typography variant="body2" sx={{ color: ink[700] }}>
                <strong>{s.n}</strong> {s.l}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Why the headline barely moves, in one line, so it does not look
            broken. It is a mean over every item and most have never been
            sent; these counts do move each time a run reports. */}
        {progress.sent.total > 0 && (
          <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${surface.line}` }}>
            <Typography variant="body2" sx={{ color: ink[700] }}>
              <strong>{progress.sent.total}</strong> items sent to a fix run so far —{' '}
              <Box component="span" sx={{ color: tone.success, fontWeight: 600 }}>{progress.sent.fixed} fixed</Box>,{' '}
              <Box component="span" sx={{ color: tone.warning, fontWeight: 600 }}>{progress.sent.partial} partly</Box>,{' '}
              <Box component="span" sx={{ color: tone.danger, fontWeight: 600 }}>{progress.sent.notFixed} not fixed</Box>
              {progress.sent.waiting > 0 ? `, ${progress.sent.waiting} waiting` : ''}.
            </Typography>
            <Typography variant="caption" sx={{ color: ink[500], display: 'block', mt: 0.25 }}>
              The other {progress.untouched} items have never been sent, and they are what holds the
              percentage down — it is an average over all {progress.total}.
              {progress.sent.unmerged > 0
                ? ` ${progress.sent.unmerged} fixed items are still on unmerged branches, so they count as 70%, not done.`
                : ''}
            </Typography>
          </Box>
        )}
      </Box>

      {data.error && <Alert severity="error">{data.error}</Alert>}
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {!data.githubReady && (
        <Alert severity="info">
          Sending straight to Claude needs one setup step: add <strong>GITHUB_TOKEN</strong> in Netlify's environment
          variables. Until then, "Copy for Claude" works.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)}>
          <ToggleButton value="open">Not done</ToggleButton>
          <ToggleButton value="problems">Needs work (audit)</ToggleButton>
          <ToggleButton value="fix_now">My "Fix now"</ToggleButton>
          <ToggleButton value="done">Done</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size="small"
          placeholder="Search items and notes"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: '1 1 220px' }}
        />
      </Box>

      <Box>
        {data.sections.map((s) => {
          const items = s.items
            .map((it) => ({ ...it, sectionNumber: s.number, sectionTitle: s.title }))
            .filter(matches);
          if (!items.length) return null;
          // The section's own progress: the mean of its items, and a count of
          // the ones actually finished. This counted rows[key].done before —
          // the tick box that no longer exists — so every section read 0.
          //
          // Always the whole section, never the filtered view: a section that
          // showed 100% because the only matching item happened to be done
          // would be worse than no number.
          const secProgs = s.items.map((it) => itemProgress(it, outcomes[it.key], verifications[it.key]));
          const secPct = Math.round(
            secProgs.reduce((a, p) => a + p.pct, 0) / Math.max(secProgs.length, 1),
          );
          const secDone = secProgs.filter((p) => p.pct === 100).length;
          const secColor = secPct === 100 ? tone.success : secPct >= 40 ? tone.warning : tone.danger;
          return (
            <Accordion key={s.number} disableGutters TransitionProps={{ unmountOnExit: true }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', width: '100%', pr: 1 }}>
                  <Typography sx={{ color: ink[500], fontVariantNumeric: 'tabular-nums', minWidth: 28 }}>{s.number}</Typography>
                  <Typography sx={{ fontWeight: 600, flex: '1 1 200px', minWidth: 0 }}>{s.title}</Typography>
                  <Box sx={{ flex: '0 0 auto', width: { xs: 120, sm: 200 } }}>
                    <ProgressBar pct={secPct} color={secColor} height={10} />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: ink[700], fontVariantNumeric: 'tabular-nums', minWidth: 94, textAlign: 'right' }}
                  >
                    <strong>{secPct}%</strong> · {secDone}/{s.items.length}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {/* The header sits inside each section rather than once at the
                    top: sections are collapsible, so a single header would
                    scroll away from the rows it names. Same GRID as the rows,
                    so the labels stay over their columns. */}
                <Box
                  sx={{
                    display: { xs: 'none', md: 'grid' },
                    gap: 1.5,
                    gridTemplateColumns: GRID,
                    pb: 0.75,
                    borderBottom: `2px solid ${surface.line}`,
                  }}
                >
                  <HeadCell>Item</HeadCell>
                  <HeadCell>Needed for</HeadCell>
                  <HeadCell>Progress</HeadCell>
                  <HeadCell>Status</HeadCell>
                  <HeadCell>Action</HeadCell>
                  <HeadCell align="right">Detail</HeadCell>
                </Box>

                {/* Whole-section actions. Forty items in a section means forty
                    clicks to send them, for what is one decision.
                    `sectionOpen` excludes anything already at 100% — those
                    rows have no tick box, and sweeping them in would ask for
                    work that is done. */}
                {(() => {
                  const sectionOpen = items
                    .filter((it) => itemProgress(it, outcomes[it.key], verifications[it.key]).pct < 100)
                    .map((it) => it.key);
                  const allPicked = sectionOpen.length > 0 && sectionOpen.every((k) => selected.includes(k));
                  if (sectionOpen.length === 0) return null;
                  return (
                    <Box
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
                        py: 1, borderBottom: `1px solid ${surface.line}`,
                      }}
                    >
                      <FormControlLabel
                        sx={{ mr: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={allPicked}
                            indeterminate={!allPicked && sectionOpen.some((k) => selected.includes(k))}
                            onChange={(e) => selectMany(sectionOpen, e.target.checked)}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Select all {sectionOpen.length} unfinished
                          </Typography>
                        }
                      />
                      <Typography variant="caption" sx={{ color: ink[500] }}>Set all to</Typography>
                      {OWNER_STATUS.filter((o) => o.value).map((o) => (
                        <Button
                          key={o.value}
                          size="small"
                          variant="outlined"
                          disabled={savingKey === 'bulk'}
                          onClick={() => setManyOwnerStatus(sectionOpen, o.value)}
                        >
                          {o.label}
                        </Button>
                      ))}
                      {savingKey === 'bulk' && (
                        <Typography variant="caption" sx={{ color: ink[500] }}>Saving…</Typography>
                      )}
                    </Box>
                  );
                })()}
                {items.map((it) => (
                  <ItemRow
                    key={it.key}
                    item={it}
                    row={rows[it.key] || {}}
                    outcome={outcomes[it.key]}
                    verification={verifications[it.key]}
                    selected={selected.includes(it.key)}
                    onToggleSelect={toggleSelect}
                    onSave={onSave}
                  />
                ))}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      {/* Collapsed by default. It only grows, and by the time there are
          thirty of them it is a wall of timestamps between the checklist
          and the send bar. The count is on the summary so it can be read
          without opening it. */}
      {(data.requests || []).length > 0 && (
        <Accordion disableGutters TransitionProps={{ unmountOnExit: true }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 600 }}>
              Issue Sent
              <Typography component="span" sx={{ color: ink[500], fontWeight: 400, ml: 1 }}>
                {data.requests.length}
              </Typography>
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Box sx={{ display: 'grid', gap: 0.75 }}>
              {data.requests.map((r) => (
                <Typography key={r.id} variant="body2">
                  {new Date(r.created_at).toLocaleString()} · {r.item_keys.length} item{r.item_keys.length === 1 ? '' : 's'} ·{' '}
                  {r.github_issue_url ? (
                    <Link href={r.github_issue_url} target="_blank" rel="noopener">Issue #{r.github_issue_number}</Link>
                  ) : 'no issue link'}
                </Typography>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {selected.length > 0 && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            bgcolor: surface.card,
            borderTop: `1px solid ${surface.line}`,
            py: 1.5,
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
            flexWrap: 'wrap',
            zIndex: 2,
          }}
        >
          <Typography sx={{ fontWeight: 600, flex: 1 }}>
            {selected.length} item{selected.length === 1 ? '' : 's'} picked
            {selected.length > MAX_SEND && ` (send ${MAX_SEND} or fewer at a time)`}
          </Typography>
          <Button onClick={() => setSelected([])}>Clear</Button>
          <Button variant="contained" startIcon={<SendIcon />} onClick={() => { setSent(null); setCopied(null); setSendOpen(true); }}>
            Send Issue
          </Button>
        </Box>
      )}

      <Dialog open={sendOpen} onClose={() => !sending && setSendOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Issue</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2 }}>
          {sent ? (
            <Alert severity="success">
              Sent. Claude will reply on{' '}
              <Link href={sent.issueUrl} target="_blank" rel="noopener">issue #{sent.issueNumber}</Link> and open a pull
              request for you to review. It won't be merged until you approve it.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" sx={{ color: ink[700] }}>
                Claude gets the audit notes for each item plus your notes and what you want done:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, maxHeight: 200, overflowY: 'auto' }}>
                {selectedItems.map((it) => (
                  <li key={it.key}>
                    <Typography variant="body2">§{it.sectionNumber} {it.label}</Typography>
                  </li>
                ))}
              </Box>
              <TextField
                label="Anything else Claude should know (optional)"
                multiline
                minRows={3}
                value={sendNote}
                onChange={(e) => setSendNote(e.target.value)}
              />
              {copied && <Alert severity="info">{copied}</Alert>}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {sent ? (
            <Button onClick={() => setSendOpen(false)}>Close</Button>
          ) : (
            <>
              <Button startIcon={<ContentCopyIcon />} onClick={copy}>Copy for Claude</Button>
              <Button onClick={() => setSendOpen(false)} disabled={sending}>Cancel</Button>
              <Button
                variant="contained"
                onClick={send}
                disabled={sending || !data.githubReady || selected.length > MAX_SEND}
                startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
              >
                {sending ? 'Sending…' : 'Send'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
